// Orkestrator E5 §4 — the one source of download versions is /downloads/releases.json (written by
// scripts/build-agent-distributions.mjs). Client-safe, pure helpers; the hook lives in api-releases.ts.

export interface ReleaseArtifact {
  name: string
  version: string
  file: string
  url: string
  bytes: number
  sha256: string
  kind: string
  os?: string
  arch?: string
  signed?: boolean
  install?: string
}

export interface ReleasesDto {
  version: number
  generatedAt: string | null
  artifacts: ReleaseArtifact[]
}

/** Public origin the copy-paste commands point at (the CLI talks to it by default). */
export const PUBLIC_ORIGIN = 'https://bystep.dev'

const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0

/** Accept only well-formed entries: a malformed file must never surface a wrong version or link. */
export function parseReleases(raw: unknown): ReleasesDto {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const list = Array.isArray(o.artifacts) ? o.artifacts : []
  const artifacts: ReleaseArtifact[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const a = entry as Record<string, unknown>
    if (!str(a.name) || !str(a.version) || !str(a.file) || !str(a.url) || !str(a.kind)) continue
    if (!/^\/downloads\/[A-Za-z0-9._-]+$/.test(a.url) && !/^https:\/\/bystep\.dev\/downloads\/[A-Za-z0-9._-]+$/.test(a.url)) continue
    artifacts.push({
      name: a.name,
      version: a.version,
      file: a.file,
      url: a.url,
      bytes: typeof a.bytes === 'number' && Number.isFinite(a.bytes) && a.bytes > 0 ? a.bytes : 0,
      sha256: str(a.sha256) && /^[0-9a-f]{64}$/.test(a.sha256) ? a.sha256 : '',
      kind: a.kind,
      ...(str(a.os) ? { os: a.os } : {}),
      ...(str(a.arch) ? { arch: a.arch } : {}),
      ...(typeof a.signed === 'boolean' ? { signed: a.signed } : {}),
      ...(str(a.install) ? { install: a.install } : {}),
    })
  }
  return { version: typeof o.version === 'number' ? o.version : 0, generatedAt: str(o.generatedAt) ? o.generatedAt : null, artifacts }
}

export const desktopArtifact = (d: ReleasesDto | null | undefined, os = 'linux', arch = 'x64'): ReleaseArtifact | null =>
  d?.artifacts.find((a) => a.kind === 'desktop' && a.os === os && a.arch === arch) ?? null

export const cliArtifact = (d: ReleasesDto | null | undefined): ReleaseArtifact | null =>
  d?.artifacts.find((a) => a.name === '@bystep/cli' && a.kind === 'npm-tarball') ?? null

/** Absolute download URL for commands people paste into a terminal. */
export const absoluteUrl = (a: ReleaseArtifact) => new URL(a.url, PUBLIC_ORIGIN).href

/** `npx` line that runs this exact CLI tarball without a global install. */
export const cliCommand = (a: ReleaseArtifact, args: string) => `npx --yes --package=${absoluteUrl(a)} bystep ${args}`

/** "119 MB" / "157 KB" — decimal units, the way download sizes are usually quoted. */
export function formatBytes(bytes: number, locale = 'en'): string {
  if (!bytes || bytes < 0) return ''
  const fmt = (n: number, digits: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n)
  if (bytes >= 1e9) return `${fmt(bytes / 1e9, 1)} GB`
  if (bytes >= 1e6) return `${fmt(bytes / 1e6, bytes >= 1e7 ? 0 : 1)} MB`
  return `${fmt(Math.max(1, bytes / 1e3), 0)} KB`
}

/** First 12 hex characters — enough to eyeball; the full value is in releases.json. */
export const shortSha = (sha: string) => (sha ? sha.slice(0, 12) : '')
